import { useState, useEffect } from "react";
import { Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { GoalData, UpsertGoalPayload } from "@/hooks/useGoals";

export interface GoalEditDialogProps {
  title: string;
  description?: string;
  goal: GoalData | null;
  year: number;
  onSave: (payload: UpsertGoalPayload) => Promise<boolean | void>;
  isSaving?: boolean;
  disabled?: boolean;
  triggerClassName?: string;
  /** When set, renders a labeled button trigger instead of the icon. */
  triggerLabel?: string;
  /** AR-314: Agency Owners also set a net commission goal next to the gross one. */
  showNetCommissionGoal?: boolean;
}

export function GoalEditDialog({
  title,
  description,
  goal,
  year,
  onSave,
  isSaving = false,
  disabled = false,
  triggerClassName,
  triggerLabel,
  showNetCommissionGoal = false,
}: GoalEditDialogProps) {
  const [open, setOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [salesGoal, setSalesGoal] = useState("");
  const [commissionGoal, setCommissionGoal] = useState("");
  const [netCommissionGoal, setNetCommissionGoal] = useState("");
  const [tripsTraveledGoal, setTripsTraveledGoal] = useState("");

  useEffect(() => {
    if (open) {
      setSaveError(null);
      const sv = parseFloat(goal?.salesGoal ?? "0");
      const cv = parseFloat(goal?.commissionGoal ?? "0");
      const nv = parseFloat(goal?.netCommissionGoal ?? "0");
      const tv = goal?.tripsTraveledGoal ?? 0;
      setSalesGoal(!isNaN(sv) && sv > 0 ? String(Math.round(sv)) : "");
      setCommissionGoal(!isNaN(cv) && cv > 0 ? String(Math.round(cv)) : "");
      setNetCommissionGoal(!isNaN(nv) && nv > 0 ? String(Math.round(nv)) : "");
      setTripsTraveledGoal(tv > 0 ? String(tv) : "");
    }
  }, [open, goal]);

  const parseGoalAmount = (raw: string): number => {
    const trimmed = raw.trim();
    if (trimmed === "") return 0;
    const n = parseFloat(trimmed);
    return !isNaN(n) && n >= 0 ? Math.round(n) : 0;
  };

  const parseGoalCount = (raw: string): number => {
    const trimmed = raw.trim();
    if (trimmed === "") return 0;
    const n = parseInt(trimmed, 10);
    return !isNaN(n) && n >= 0 ? n : 0;
  };

  const handleSave = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setSaveError(null);

    const payload: UpsertGoalPayload = { year };
    if (salesGoal.trim() !== "") {
      payload.salesGoal = String(parseGoalAmount(salesGoal));
    }
    if (commissionGoal.trim() !== "") {
      payload.commissionGoal = String(parseGoalAmount(commissionGoal));
    }
    if (showNetCommissionGoal && netCommissionGoal.trim() !== "") {
      payload.netCommissionGoal = String(parseGoalAmount(netCommissionGoal));
    }
    if (tripsTraveledGoal.trim() !== "") {
      payload.tripsTraveledGoal = parseGoalCount(tripsTraveledGoal);
    }

    try {
      const result = await onSave(payload);
      if (result !== false) {
        setOpen(false);
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save goals");
    } finally {
      setIsSubmitting(false);
    }
  };

  const trigger = triggerLabel ? (
    <DialogTrigger asChild>
      <Button variant="default" size="sm" className={triggerClassName} disabled={disabled}>
        {triggerLabel}
      </Button>
    </DialogTrigger>
  ) : (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={triggerClassName ?? "h-11 w-11 md:h-7 md:w-7"}
              disabled={disabled}
              aria-label={`Edit ${title}`}
            >
              <Target className="h-4 w-4" />
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>{disabled ? "View goals" : `Set ${title}`}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger}
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="salesGoal">Year Sales Goal ($)</Label>
            <Input
              id="salesGoal"
              type="number"
              min="0"
              step="1"
              value={salesGoal}
              onChange={(e) => setSalesGoal(e.target.value)}
              placeholder="0"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="commissionGoal">
              {showNetCommissionGoal ? "Year Gross Commission Goal ($)" : "Year Commission Goal ($)"}
            </Label>
            <Input
              id="commissionGoal"
              type="number"
              min="0"
              step="1"
              value={commissionGoal}
              onChange={(e) => setCommissionGoal(e.target.value)}
              placeholder="0"
            />
          </div>
          {showNetCommissionGoal && (
            <div className="space-y-2">
              <Label htmlFor="netCommissionGoal">Year Net Commission Goal ($)</Label>
              <Input
                id="netCommissionGoal"
                type="number"
                min="0"
                step="1"
                value={netCommissionGoal}
                onChange={(e) => setNetCommissionGoal(e.target.value)}
                placeholder="0"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="tripsTraveledGoal">Year Trips Traveled Goal</Label>
            <Input
              id="tripsTraveledGoal"
              type="number"
              min="0"
              step="1"
              value={tripsTraveledGoal}
              onChange={(e) => setTripsTraveledGoal(e.target.value)}
              placeholder="0"
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Not sure where to start? Try our{" "}
          <a
            href="https://worldvia.pro/pro-s_plan"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline hover:text-primary/80"
          >
            Sales Plan Generator
          </a>
          .
        </p>
        {saveError && (
          <p className="text-sm text-destructive" role="alert">
            {saveError}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isSubmitting}>
            {isSaving || isSubmitting ? "Saving..." : "Save Goals"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Standalone clickable goal icon for reuse on other pages (e.g., Reporting). */
export function GoalEditTriggerIcon({
  onClick,
  disabled,
  className,
  tooltip = "Set Goals",
}: {
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  tooltip?: string;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={className ?? "h-11 w-11 md:h-7 md:w-7"}
            disabled={disabled}
            onClick={onClick}
            aria-label={tooltip}
          >
            <Target className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
